import React, { useState } from 'react';
import { useContent } from '../../contexts/ContentContext';
import ImageUpload from './ImageUpload';
import ImagePositionEditor from './ImagePositionEditor';
import { resolveWhatsAppPhone } from '../../utils/whatsapp';
import { getMediaUrl } from '../../utils/mediaUrl';
import { validateYouTubeUrl, extractYouTubeVideoId, generateYouTubeThumbnail } from '../../utils/youtubeUtils';
import './SimpleFormationsAdmin.css';
import './FeaturedProgramsAdmin.css';

const SimpleFormationsAdmin = () => {
  const { content, updateContent, getContent } = useContent();
  const [expandedFormation, setExpandedFormation] = useState(null);
  const [editingImageIndex, setEditingImageIndex] = useState(null);
  const [editingImageType, setEditingImageType] = useState(null);
  
  const formations = content?.formations?.formationsGrid?.formations || [];
  const gridMeta = content?.formations?.formationsGrid || {};
  const specialties = content?.formations?.specialties || [];

  const handleFieldChange = (formationIndex, field, value) => {
    const updatedFormations = [...formations];
    updatedFormations[formationIndex] = {
      ...updatedFormations[formationIndex],
      [field]: value
    };
    
    updateContent('formations.formationsGrid.formations', updatedFormations);
  };

  const handleImagePositionSave = (formationIndex, imageType, settings) => {
    console.log('💾 Saving image position:', {
      formationIndex,
      imageType,
      settings,
      formationTitle: formations[formationIndex]?.title
    });
    
    const settingsField = imageType === 'formation' ? 'formationPhotoSettings' : 'instructorPhotoSettings';
    handleFieldChange(formationIndex, settingsField, settings);
    setEditingImageIndex(null);
    setEditingImageType(null);
    
    // Force a small delay to ensure save completes
    setTimeout(() => {
      console.log('✅ Image position saved and editor closed');
    }, 500);
  };

  const handleArrayFieldChange = (formationIndex, field, arrayIndex, value) => {
    const updatedFormations = [...formations];
    const updatedArray = [...(updatedFormations[formationIndex][field] || [])];
    updatedArray[arrayIndex] = value;
    updatedFormations[formationIndex] = {
      ...updatedFormations[formationIndex],
      [field]: updatedArray
    };
    
    updateContent('formations.formationsGrid.formations', updatedFormations);
  };

  const addArrayItem = (formationIndex, field, template) => {
    const updatedFormations = [...formations];
    const currentArray = updatedFormations[formationIndex][field] || [];
    updatedFormations[formationIndex] = {
      ...updatedFormations[formationIndex],
      [field]: [...currentArray, template]
    };
    
    updateContent('formations.formationsGrid.formations', updatedFormations);
  };

  const removeArrayItem = (formationIndex, field, arrayIndex) => {
    const updatedFormations = [...formations];
    const updatedArray = [...(updatedFormations[formationIndex][field] || [])];
    updatedArray.splice(arrayIndex, 1);
    updatedFormations[formationIndex] = {
      ...updatedFormations[formationIndex],
      [field]: updatedArray
    };
    
    updateContent('formations.formationsGrid.formations', updatedFormations);
  };

  const moveArrayItem = (formationIndex, field, arrayIndex, direction) => {
    const updatedFormations = [...formations];
    const updatedArray = [...(updatedFormations[formationIndex][field] || [])];
    const targetIndex = direction === 'up' ? arrayIndex - 1 : arrayIndex + 1;
    if (targetIndex < 0 || targetIndex >= updatedArray.length) return;
    [updatedArray[arrayIndex], updatedArray[targetIndex]] = [
      updatedArray[targetIndex],
      updatedArray[arrayIndex],
    ];
    updatedFormations[formationIndex] = {
      ...updatedFormations[formationIndex],
      [field]: updatedArray,
    };
    updateContent('formations.formationsGrid.formations', updatedFormations);
  };

  const handleReviewYouTubeChange = (formationIndex, reviewIndex, review, url) => {
    const next = {
      ...review,
      youtube_url: url,
      youtube_id: validateYouTubeUrl(url) ? extractYouTubeVideoId(url) : '',
      video_thumbnail: validateYouTubeUrl(url) ? generateYouTubeThumbnail(extractYouTubeVideoId(url)) : '',
    };
    handleArrayFieldChange(formationIndex, 'reviews', reviewIndex, next);
  };

  const toggleFormation = (index) => {
    setExpandedFormation(expandedFormation === index ? null : index);
  };

  const handleAddFormation = () => {
    const newFormation = {
      id: `formation-${Date.now()}`,
      title: 'Nouvelle Formation',
      subtitle: 'Basique - Avancée',
      instructor: 'Dr. Instructeur',
      color: 'blue',
      description: 'Description de la formation',
      formationPhoto: '',
      instructorPhoto: '',
      instructorTitle: 'Formateur Expert',
      instructorBio: 'Biographie de l\'instructeur',
      objectives: [],
      program: [],
      brochure: ''
    };
    const updatedFormations = [...formations, newFormation];
    updateContent('formations.formationsGrid.formations', updatedFormations);
    setExpandedFormation(formations.length); // Ouvre la nouvelle formation
  };

  const handleDeleteFormation = (index) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette formation ?')) {
      const updatedFormations = formations.filter((_, i) => i !== index);
      updateContent('formations.formationsGrid.formations', updatedFormations);
      setExpandedFormation(null);
    }
  };

  const handleMoveFormation = (index, direction) => {
    const updatedFormations = [...formations];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= updatedFormations.length) return;

    [updatedFormations[index], updatedFormations[targetIndex]] = [
      updatedFormations[targetIndex],
      updatedFormations[index],
    ];
    updateContent('formations.formationsGrid.formations', updatedFormations);

    if (expandedFormation === index) {
      setExpandedFormation(targetIndex);
    } else if (expandedFormation === targetIndex) {
      setExpandedFormation(index);
    }
  };

  const formationCount = formations.length;

  return (
    <div className="simple-formations-admin">
      <div className="section-settings" style={{ marginBottom: '1.5rem' }}>
        <div className="setting-group">
          <label>Titre du catalogue</label>
          <input
            type="text"
            value={gridMeta.title || ''}
            onChange={(e) => updateContent('formations.formationsGrid.title', e.target.value)}
            placeholder="Catalogue des Formations"
          />
        </div>
        <div className="setting-group">
          <label>Sous-titre du catalogue</label>
          <textarea
            value={gridMeta.subtitle || ''}
            onChange={(e) => updateContent('formations.formationsGrid.subtitle', e.target.value)}
            placeholder="Découvrez nos programmes..."
            rows={2}
          />
        </div>
      </div>

      <div className="formations-admin-header">
        <div>
          <h2>Gestion des formations</h2>
          <p>{formations.length} formations — modifiez textes, photos, dates et WhatsApp. Utilisez ↑ ↓ pour l&apos;ordre d&apos;affichage sur le site.</p>
        </div>
        <button
          onClick={handleAddFormation}
          style={{
            padding: '0.75rem 1.5rem',
            background: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            boxShadow: '0 2px 8px rgba(40,167,69,0.3)'
          }}
        >
          <i className="fas fa-plus"></i>
          Ajouter une formation
        </button>
      </div>

      <div className="formations-list">
        {formations.map((formation, index) => (
          <div key={formation.id || index} className="formation-admin-card">
            
            {/* Formation Header */}
            <div className="formation-header">
              <div className="formation-title-info" onClick={() => toggleFormation(index)}>
                <h3>{formation.title || 'Formation sans titre'}</h3>
                <p>{formation.subtitle} • Instructeur: {formation.instructor}</p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button
                  type="button"
                  className="btn-move"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMoveFormation(index, 'up');
                  }}
                  disabled={index === 0}
                  title="Remonter"
                  aria-label="Remonter cette formation"
                >
                  <i className="fas fa-arrow-up" />
                </button>
                <button
                  type="button"
                  className="btn-move"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMoveFormation(index, 'down');
                  }}
                  disabled={index === formationCount - 1}
                  title="Descendre"
                  aria-label="Descendre cette formation"
                >
                  <i className="fas fa-arrow-down" />
                </button>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteFormation(index);
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem'
                  }}
                  title="Supprimer cette formation"
                >
                  <i className="fas fa-trash"></i>
                  Supprimer
                </button>
                <button className="toggle-btn" onClick={() => toggleFormation(index)}>
                  {expandedFormation === index ? '▼' : '▶'}
                </button>
              </div>
            </div>

            {/* Formation Content - Expanded */}
            {expandedFormation === index && (
              <div className="formation-content">
                
                {/* Basic Info */}
                <div className="section">
                  <h4>Information de base</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Titre</label>
                      <input 
                        type="text" 
                        value={formation.title || ''} 
                        onChange={(e) => handleFieldChange(index, 'title', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Sous-titre</label>
                      <input 
                        type="text" 
                        value={formation.subtitle || ''} 
                        onChange={(e) => handleFieldChange(index, 'subtitle', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Spécialité (specialtyId)</label>
                      <select
                        value={formation.specialtyId || ''}
                        onChange={(e) => handleFieldChange(index, 'specialtyId', e.target.value)}
                      >
                        <option value="">— Non assignée —</option>
                        {specialties.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.title} ({s.id})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Slug URL</label>
                      <input
                        type="text"
                        value={formation.slug || formation.id || ''}
                        onChange={(e) => handleFieldChange(index, 'slug', e.target.value)}
                        placeholder="ex: implantologie-cycle-basique"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Instructeur</label>
                      <input 
                        type="text" 
                        value={formation.instructor || ''} 
                        onChange={(e) => handleFieldChange(index, 'instructor', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Icône (si pas de photo)</label>
                      <input
                        type="text"
                        value={formation.icon || ''}
                        onChange={(e) => handleFieldChange(index, 'icon', e.target.value)}
                        placeholder="fas fa-tooth"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Couleur</label>
                      <select 
                        value={formation.color || ''} 
                        onChange={(e) => handleFieldChange(index, 'color', e.target.value)}
                      >
                        <option value="">Choisir une couleur</option>
                        <option value="blue">Bleu</option>
                        <option value="green">Vert</option>
                        <option value="purple">Violet</option>
                        <option value="red">Rouge</option>
                        <option value="orange">Orange</option>
                        <option value="cyan">Cyan</option>
                        <option value="pink">Rose</option>
                        <option value="yellow">Jaune</option>
                        <option value="indigo">Indigo</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <textarea 
                      value={formation.description || ''} 
                      onChange={(e) => handleFieldChange(index, 'description', e.target.value)}
                      rows={3}
                    />
                  </div>

                  <div className="form-group">
                    <label>Message WhatsApp</label>
                    <textarea
                      value={formation.whatsappProgramMessage || ''}
                      onChange={(e) => handleFieldChange(index, 'whatsappProgramMessage', e.target.value)}
                      rows={2}
                      placeholder="Bonjour, je souhaite des informations sur la formation : ..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Numéro WhatsApp pour cette formation</label>
                    <input
                      type="tel"
                      value={formation.whatsappPhone || ''}
                      onChange={(e) => handleFieldChange(index, 'whatsappPhone', e.target.value)}
                      placeholder={resolveWhatsAppPhone(formation, getContent) || '+213797607481'}
                    />
                    <small style={{ color: '#64748b' }}>
                      Indicatif pays inclus (ex: +213797607481). Laissez vide pour utiliser le numéro principal.
                    </small>
                  </div>
                </div>

                {/* Images */}
                <div className="section">
                  <h4>Image de formation</h4>
                  <div className="image-upload-group">
                    <label>Photo de formation (Proportion 16:9)</label>
                    <ImageUpload
                      value={formation.formationPhoto || ''}
                      onChange={(value) => handleFieldChange(index, 'formationPhoto', value)}
                      placeholder="Télécharger photo de formation"
                    />
                    <small style={{color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '8px'}}>
                      Recommandé: 1600x900px (proportion 16:9)
                    </small>
                    
                    {formation.formationPhoto && (
                      <div style={{ marginTop: '1rem' }}>
                        {editingImageIndex === index && editingImageType === 'formation' ? (
                          <ImagePositionEditor
                            imageUrl={getMediaUrl(formation.formationPhoto)}
                            currentSettings={formation.formationPhotoSettings}
                            onSave={(settings) => handleImagePositionSave(index, 'formation', settings)}
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingImageIndex(index);
                              setEditingImageType('formation');
                            }}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              background: '#667eea',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.5rem',
                              cursor: 'pointer',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.5rem'
                            }}
                          >
                            <i className="fas fa-sliders-h"></i>
                            Ajuster position et zoom
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Instructor Details */}
                <div className="section">
                  <h4>Détails de l'Instructeur</h4>
                  <div className="image-upload-group">
                    <label>Photo de l'instructeur</label>
                    <ImageUpload
                      value={formation.instructorPhoto || ''}
                      onChange={(value) => handleFieldChange(index, 'instructorPhoto', value)}
                      placeholder="Télécharger photo instructeur"
                    />
                    {formation.instructorPhoto && (
                      <div style={{ marginTop: '1rem' }}>
                        {editingImageIndex === index && editingImageType === 'instructor' ? (
                          <ImagePositionEditor
                            imageUrl={getMediaUrl(formation.instructorPhoto)}
                            currentSettings={formation.instructorPhotoSettings}
                            onSave={(settings) => handleImagePositionSave(index, 'instructor', settings)}
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingImageIndex(index);
                              setEditingImageType('instructor');
                            }}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              background: '#667eea',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.5rem',
                              cursor: 'pointer',
                              fontWeight: '500',
                            }}
                          >
                            <i className="fas fa-sliders-h"></i> Ajuster photo instructeur
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Titre de l'instructeur</label>
                    <input 
                      type="text" 
                      value={formation.instructorTitle || ''} 
                      onChange={(e) => handleFieldChange(index, 'instructorTitle', e.target.value)}
                      placeholder="Ex: Formateur Expert, Chirurgien-Dentiste, Professeur..."
                    />
                  </div>
                  <div className="form-group">
                    <label>Biographie de l'instructeur</label>
                    <textarea 
                      value={formation.instructorBio || ''} 
                      onChange={(e) => handleFieldChange(index, 'instructorBio', e.target.value)}
                      rows={3}
                      placeholder="Courte description de l'instructeur, son expérience, ses qualifications..."
                    />
                  </div>
                  <div className="form-group">
                    <label>CV du formateur (URL PDF)</label>
                    <input
                      type="text"
                      value={formation.instructorCV || ''}
                      onChange={(e) => handleFieldChange(index, 'instructorCV', e.target.value)}
                      placeholder="https://.../cv.pdf"
                    />
                  </div>
                </div>

                {/* Niveaux */}
                <div className="section">
                  <h4>Niveaux</h4>
                  {(formation.level || []).map((level, levelIndex) => (
                    <div key={levelIndex} className="array-item">
                      <input
                        type="text"
                        value={level || ''}
                        onChange={(e) => handleArrayFieldChange(index, 'level', levelIndex, e.target.value)}
                        placeholder="Ex: Basique, Avancé"
                      />
                      <button onClick={() => removeArrayItem(index, 'level', levelIndex)} className="remove-btn">✕</button>
                    </div>
                  ))}
                  <button onClick={() => addArrayItem(index, 'level', '')} className="add-btn">+ Ajouter niveau</button>
                </div>

                {/* Brochure */}
                <div className="section">
                  <h4>Brochure PDF</h4>
                  <div className="form-group">
                    <input
                      type="text"
                      value={formation.brochure || ''}
                      onChange={(e) => handleFieldChange(index, 'brochure', e.target.value)}
                      placeholder="URL du PDF brochure"
                    />
                  </div>
                </div>

                {/* Objectives */}
                <div className="section">
                  <h4>Objectifs</h4>
                  {(formation.objectives || []).map((objective, objIndex) => (
                    <div key={objIndex} className="array-item">
                      <input 
                        type="text" 
                        value={objective || ''} 
                        onChange={(e) => handleArrayFieldChange(index, 'objectives', objIndex, e.target.value)}
                        placeholder="Objectif de formation"
                      />
                      <button 
                        onClick={() => removeArrayItem(index, 'objectives', objIndex)}
                        className="remove-btn"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button 
                    onClick={() => addArrayItem(index, 'objectives', '')}
                    className="add-btn"
                  >
                    + Ajouter objectif
                  </button>
                </div>

                {/* Programme */}
                <div className="section">
                  <h4>Programme (modules)</h4>
                  {(formation.program || []).map((module, programIndex) => (
                    <div key={programIndex} className="program-module" style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                      <div className="form-row">
                        <input
                          type="text"
                          value={module.module || ''}
                          onChange={(e) => handleArrayFieldChange(index, 'program', programIndex, { ...module, module: e.target.value })}
                          placeholder="Module 1"
                        />
                        <input
                          type="text"
                          value={module.duration || ''}
                          onChange={(e) => handleArrayFieldChange(index, 'program', programIndex, { ...module, duration: e.target.value })}
                          placeholder="Durée"
                        />
                        <button onClick={() => removeArrayItem(index, 'program', programIndex)} className="remove-btn">✕</button>
                      </div>
                      <input
                        type="text"
                        value={module.title || ''}
                        onChange={(e) => handleArrayFieldChange(index, 'program', programIndex, { ...module, title: e.target.value })}
                        placeholder="Titre du module"
                        style={{ width: '100%', marginBottom: '0.5rem' }}
                      />
                      {(module.content || []).map((line, lineIndex) => (
                        <div key={lineIndex} className="array-item">
                          <input
                            type="text"
                            value={line || ''}
                            onChange={(e) => {
                              const content = [...(module.content || [])];
                              content[lineIndex] = e.target.value;
                              handleArrayFieldChange(index, 'program', programIndex, { ...module, content });
                            }}
                            placeholder="Point du programme"
                          />
                          <button
                            onClick={() => {
                              const content = (module.content || []).filter((_, i) => i !== lineIndex);
                              handleArrayFieldChange(index, 'program', programIndex, { ...module, content });
                            }}
                            className="remove-btn"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          const content = [...(module.content || []), ''];
                          handleArrayFieldChange(index, 'program', programIndex, { ...module, content });
                        }}
                        className="add-btn"
                        type="button"
                      >
                        + Point du module
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => addArrayItem(index, 'program', { module: '', title: '', duration: '', content: [] })}
                    className="add-btn"
                    type="button"
                  >
                    + Ajouter module
                  </button>
                </div>

                {/* Sessions */}
                <div className="section">
                  <h4>Sessions</h4>
                  {(formation.sessions || []).map((session, sessionIndex) => (
                    <div key={sessionIndex} className="session-item">
                      <div className="form-row">
                        <input 
                          type="text" 
                          value={session.date || ''} 
                          onChange={(e) => handleArrayFieldChange(index, 'sessions', sessionIndex, { ...session, date: e.target.value })}
                          placeholder="Date"
                        />
                        <input 
                          type="text" 
                          value={session.location || ''} 
                          onChange={(e) => handleArrayFieldChange(index, 'sessions', sessionIndex, { ...session, location: e.target.value })}
                          placeholder="Lieu"
                        />
                        <input 
                          type="text" 
                          value={session.spots || ''} 
                          onChange={(e) => handleArrayFieldChange(index, 'sessions', sessionIndex, { ...session, spots: e.target.value })}
                          placeholder="Places disponibles"
                        />
                        <button 
                          onClick={() => removeArrayItem(index, 'sessions', sessionIndex)}
                          className="remove-btn"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                  <button 
                    onClick={() => addArrayItem(index, 'sessions', { date: '', location: '', spots: '' })}
                    className="add-btn"
                  >
                    + Ajouter session
                  </button>
                </div>

                {/* Reviews */}
                <div className="section">
                  <h4>Avis (texte ou vidéo YouTube)</h4>
                  {(formation.reviews || []).map((review, reviewIndex) => (
                    <div key={reviewIndex} className="review-item">
                      <div className="form-row">
                        <input 
                          type="text" 
                          value={review.name || ''} 
                          onChange={(e) => handleArrayFieldChange(index, 'reviews', reviewIndex, { ...review, name: e.target.value })}
                          placeholder="Nom"
                        />
                        <select
                          value={review.type || 'text'}
                          onChange={(e) => handleArrayFieldChange(index, 'reviews', reviewIndex, {
                            ...review,
                            type: e.target.value,
                            ...(e.target.value === 'text'
                              ? { youtube_url: '', youtube_id: '', video_thumbnail: '' }
                              : { comment: '' }),
                          })}
                        >
                          <option value="text">Avis texte</option>
                          <option value="video">Avis vidéo</option>
                        </select>
                        <select 
                          value={review.rating || 5} 
                          onChange={(e) => handleArrayFieldChange(index, 'reviews', reviewIndex, { ...review, rating: parseInt(e.target.value) })}
                        >
                          <option value={5}>5 étoiles</option>
                          <option value={4}>4 étoiles</option>
                          <option value={3}>3 étoiles</option>
                          <option value={2}>2 étoiles</option>
                          <option value={1}>1 étoile</option>
                        </select>
                        <input 
                          type="text" 
                          value={review.date || ''} 
                          onChange={(e) => handleArrayFieldChange(index, 'reviews', reviewIndex, { ...review, date: e.target.value })}
                          placeholder="Date"
                        />
                        <button
                          type="button"
                          className="btn-move"
                          onClick={() => moveArrayItem(index, 'reviews', reviewIndex, 'up')}
                          disabled={reviewIndex === 0}
                          title="Remonter"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="btn-move"
                          onClick={() => moveArrayItem(index, 'reviews', reviewIndex, 'down')}
                          disabled={reviewIndex === (formation.reviews || []).length - 1}
                          title="Descendre"
                        >
                          ↓
                        </button>
                        <button 
                          onClick={() => removeArrayItem(index, 'reviews', reviewIndex)}
                          className="remove-btn"
                        >
                          ✕
                        </button>
                      </div>
                      {(review.type || 'text') === 'video' ? (
                        <input
                          type="url"
                          value={review.youtube_url || ''}
                          onChange={(e) => handleReviewYouTubeChange(index, reviewIndex, review, e.target.value)}
                          placeholder="https://www.youtube.com/watch?v=..."
                        />
                      ) : (
                        <textarea 
                          value={review.comment || ''} 
                          onChange={(e) => handleArrayFieldChange(index, 'reviews', reviewIndex, { ...review, comment: e.target.value })}
                          placeholder="Commentaire"
                          rows={2}
                        />
                      )}
                    </div>
                  ))}
                  <button 
                    onClick={() => addArrayItem(index, 'reviews', { name: '', rating: 5, comment: '', date: '', type: 'text' })}
                    className="add-btn"
                  >
                    + Ajouter avis
                  </button>
                </div>

              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SimpleFormationsAdmin;
